import React, { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import type { Order, CallBlock, WindowBucket } from "~/features/orders/types-order";
import type { Step, ActivityEvent, CallState, EditingState, OutcomeStats, LiveTransfer, Toast } from "~/features/orders/types";
import { secureRandom } from "~/shared/utils/random";
import { TransferQueueTable } from "~/features/transfer-queue/components/TransferQueueTable";
import { Step3Review } from "~/features/orders/components/Step3Review";
import { EditModal } from "~/features/orders/components/EditModal";
import { Toast as ToastComponent } from "~/shared/components/Toast";
import { CenteredLoading } from "~/shared/components/CenteredLoading";
import { ErrorState } from "~/shared/components/ErrorState";
import {
  fetchTransferQueue,
  updateTransferQueue,
  lockTransferQueueOrder,
  startManualTransferQueueCall,
  unlockTransferQueueOrder,
} from "~/features/transfer-queue/api";
import type { TransferQueueRecord, TransferQueuePagination } from "~/features/transfer-queue/types";
import { configureMsalInstance } from "~/services/api-client";
import { ApiError } from "~/shared/utils/api-error";
import { useLocationCode } from "~/shared/contexts/LocationCodeContext";
import { useDeliveryWindows } from "~/shared/contexts/DeliveryWindowsContext";
import { useTransferQueueWebSocket, type TransferQueueWsEvent } from "~/features/transfer-queue/hooks";
import { useTokenErrorHandler } from "~/shared/hooks/useTokenErrorHandler";
import { useServerSearch } from "~/shared/hooks/useServerSearch";
import { usePersistedFilter } from "~/shared/hooks/usePersistedFilter";
import type { CallFlowDisplayName } from "~/features/orders/constants";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 200] as const;

type TransferQueueWindowPayload = {
  start: number;
  end: number;
  label?: string;
  startMinutes?: number;
  endMinutes?: number;
};

/** Window sent on a Transfer Queue update; keeps exact minutes so an unnamed/custom window is sent unchanged. */
function toTransferQueueWindowPayload(window: WindowBucket | null): TransferQueueWindowPayload | null {
  if (!window) return null;
  const payload: TransferQueueWindowPayload = { start: window.start, end: window.end };
  if (window.label != null) payload.label = window.label;
  if (window.startMinutes != null && window.endMinutes != null) {
    payload.startMinutes = window.startMinutes;
    payload.endMinutes = window.endMinutes;
  }
  return payload;
}

type TransferQueueLocationState = {
  block: CallBlock;
  orders: Order[];
  selectedIds: string[];
  flow?: CallFlowDisplayName;
};

export default function TransferQueue() {
  const location = useLocation();
  const navigate = useNavigate();
  const { instance: msalInstance, accounts } = useMsal();
  const locationState = location.state as TransferQueueLocationState | null;

  const [step] = useState<Step>(2);
  const [block] = useState<CallBlock | null>(locationState?.block ?? null);
  const [orders] = useState<Order[]>(locationState?.orders ?? []);
  const [records, setRecords] = useState<TransferQueueRecord[]>([]);
  const allCitiesSeenRef = useRef<Set<string>>(new Set());
  const [allCities, setAllCities] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<typeof PAGE_SIZE_OPTIONS[number]>(PAGE_SIZE_OPTIONS[4]);
  const [pagination, setPagination] = useState<TransferQueuePagination>({
    total: 0,
    page: 1,
    limit: PAGE_SIZE_OPTIONS[4],
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dial, setDial] = useState<Record<string, CallState>>({});
  const [progress, setProgress] = useState({ total: 0, completed: 0, paused: false });
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [editingInline, setEditingInline] = useState<string | null>(null);
  const [handledTransfers, setHandledTransfers] = useState<Set<string>>(new Set());
  const [activityFeed, setActivityFeed] = useState<ActivityEvent[]>([]);
  const [outcomeStats, setOutcomeStats] = useState<OutcomeStats>({ confirmed: 0, alt_window: 0, reschedule: 0, no_answer: 0, transfer: 0 });
  const [liveTransfers, setLiveTransfers] = useState<LiveTransfer[]>([]);
  const [toast, setToast] = useState<Toast>(null);
  const timersRef = useRef<number[]>([]);
  const activityIdRef = useRef(0);
  const hasLoadedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isLoadingRef = useRef(false);
  const isInitialMount = useRef(true);

  // Server-side column filters — persisted to localStorage across refreshes
  const [cityFilter, setCityFilter, cityFilterRef] = usePersistedFilter('tq:city');
  const [statusFilter, setStatusFilter] = usePersistedFilter('tq:status');
  const [outcomeFilter, setOutcomeFilter] = usePersistedFilter('tq:outcome');
  const [whiteGloveFilter, setWhiteGloveFilter] = usePersistedFilter('tq:whiteGlove');

  const { selectedLocationCode } = useLocationCode();
  const selectedLocationCodeRef = useRef(selectedLocationCode);
  const { jobLocations } = useDeliveryWindows();
  const { handleTokenError, resetTokenErrorRetry } = useTokenErrorHandler(msalInstance);

  // ── WebSocket: derive userId & jobLocations for real-time updates ──
  const currentUserId = useMemo(() => {
    const email = accounts[0]?.username ?? '';
    const prefix = email.split('@')[0]?.toUpperCase() ?? '';
    return prefix ? `BAYSHORE\\${prefix}` : '';
  }, [accounts]);


  const handleTransferQueueWsEvent = useCallback((event: TransferQueueWsEvent) => {
    console.log('[TransferQueue] WS event:', event.action, event.transferQueue);
    const incoming = event.transferQueue as unknown as TransferQueueRecord & { locationCode?: string };
    if (!incoming?.orderId) return;

    if (event.action === 'created') {
      setRecords(prev => {
        if (prev.some(r => r.orderId === incoming.orderId)) return prev;
        return [incoming, ...prev];
      });
    } else if (event.action === 'updated') {
      const incomingStatus = (incoming.queueStatus as string | undefined)?.toLowerCase();
      // Terminal status — remove the order from the transfer queue list
      if (
        incomingStatus === 'completed' ||
        incomingStatus === 'archived'
      ) {
        // Terminal status — remove the order from the transfer queue list
        setRecords(prev => prev.filter(r => r.orderId !== incoming.orderId));
      } else {
        // Non-terminal update — merge in place
        setRecords(prev =>
          prev.map(r => r.orderId === incoming.orderId ? { ...r, ...incoming } : r)
        );
      }
    } else if (event.action === 'deleted') {
      setRecords(prev => prev.filter(r => r.orderId !== incoming.orderId));
    }
  }, []);

  useTransferQueueWebSocket({
    userId: currentUserId,
    jobLocations,
    onTransferQueueEvent: handleTransferQueueWsEvent,
    enabled: true,
  });

  // Load a paginated page of transfer queue records
  function buildFilters() {
    const loc = selectedLocationCodeRef.current;
    return {
      cities: cityFilterRef.current.length > 0 ? cityFilterRef.current : undefined,
      locationCodes: loc ? [loc] : undefined,
    };
  }

  function extractErrorMessage(err: unknown): string {
    if (err instanceof ApiError) return err.message;
    if (err instanceof Error) return err.message;
    return "Failed to load transfer queue";
  }

  const loadTransferQueue = useCallback(async (reqPage?: number, reqLimit?: number) => {
    if (isLoadingRef.current) return;

    const p = reqPage ?? page;
    const lim = reqLimit ?? pageSize;

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      configureMsalInstance(msalInstance);
      const { records: apiRecords, pagination: pag, contextualCities } = await fetchTransferQueue(
        p,
        lim,
        searchQueryRef.current ?? undefined,
        buildFilters()
      );
      setRecords(apiRecords);
      setPagination(pag);
      contextualCities.forEach(c => allCitiesSeenRef.current.add(c));
      setAllCities(Array.from(allCitiesSeenRef.current).sort((a, b) => a.localeCompare(b)));
      setPage(pag.page);
      setError(null);
      resetTokenErrorRetry();
      hasLoadedRef.current = true;
    } catch (err: unknown) {
      if (err instanceof Error && (err.name === 'AbortError' || err.message.includes('cancel'))) return;
      if (handleTokenError(err, () => loadTransferQueue(p, lim), () => { isLoadingRef.current = false; })) return;
      const errorMessage = extractErrorMessage(err);
      setError(errorMessage);
      setToast({ message: errorMessage, type: "error" });
      setTimeout(() => setToast(null), 5000);
      console.error("Failed to load transfer queue:", err);
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, [page, pageSize, msalInstance, handleTokenError, resetTokenErrorRetry]);

  // Load on mount
  useEffect(() => {
    loadTransferQueue(1, PAGE_SIZE_OPTIONS[4]);
    return () => { abortControllerRef.current?.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when page or pageSize changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) { isInitialMount.current = false; return; }
    loadTransferQueue(page, pageSize);
  }, [page, pageSize, loadTransferQueue]);

  useEffect(() => { selectedLocationCodeRef.current = selectedLocationCode; }, [selectedLocationCode]);

  // Ref to always hold the latest loadTransferQueue for filter/search triggers
  const loadRef = useRef(loadTransferQueue);
  useEffect(() => { loadRef.current = loadTransferQueue; }, [loadTransferQueue]);

  // Server-side search (debounced input + immediate Enter)
  const { searchQuery, searchQueryRef, handleSearchChange, handleSearchSubmit } = useServerSearch({
    onSearch: () => loadRef.current(1, pageSize),
  });

  // Handle city filter change — triggers API call immediately
  const handleCityFilterChange = useCallback((selected: string[]) => {
    setCityFilter(selected);
    loadRef.current(1, pageSize);
  }, [pageSize, setCityFilter]);

  // Handle status filter change — client-side only, no API reload
  const handleStatusFilterChange = useCallback((selected: string[]) => {
    setStatusFilter(selected);
  }, [setStatusFilter]);

  // Handle outcome filter change — client-side only, no API reload
  const handleOutcomeFilterChange = useCallback((selected: string[]) => {
    setOutcomeFilter(selected);
  }, [setOutcomeFilter]);

  // Handle whiteGlove filter change — client-side only, no API reload
  const handleWhiteGloveFilterChange = useCallback((selected: string[]) => {
    setWhiteGloveFilter(selected);
  }, [setWhiteGloveFilter]);

  // Re-fetch records when location changes (server-side filtering)
  const prevLocationRef = useRef(selectedLocationCode);
  useEffect(() => {
    if (prevLocationRef.current === selectedLocationCode) return;
    prevLocationRef.current = selectedLocationCode;
    if (!hasLoadedRef.current) return;
    loadRef.current(1, pageSize);
  }, [selectedLocationCode, pageSize]);

  // Activity feed helper
  function addActivity(orderId: string, message: string, type: ActivityEvent['type']) {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;
    activityIdRef.current += 1;
    const event: ActivityEvent = {
      id: activityIdRef.current,
      orderId,
      smNumber: order.sm_number,
      patientName: order.patient_name,
      message,
      type,
      timestamp: Date.now()
    };
    setActivityFeed(prev => [...prev, event]);
  }

  // Initialize calling when component receives state
  useEffect(() => {
    if (locationState && locationState.selectedIds.length > 0) {
      const { selectedIds } = locationState;

      setActivityFeed([]);
      setOutcomeStats({ confirmed: 0, alt_window: 0, reschedule: 0, no_answer: 0, transfer: 0 });
      setHandledTransfers(new Set());
      setLiveTransfers([]);

      const init: Record<string, CallState> = {};
      selectedIds.forEach(id => init[id] = 'pending');
      setDial(init);
      setProgress({ total: selectedIds.length, completed: 0, paused: false });

      // Start the simulated dialer
      runDialer(locationState.block.id, selectedIds, init);
    }
  }, []);

  function runDialer(blockId: number, ids: string[], state: Record<string, CallState>) {
    const concurrency = 4;
    function kick() {
      if (progress.paused) return;
      while (Object.values(state).filter(s => s === 'ringing' || s === 'connected' || s === 'talking' || s === 'transfer_requested').length < concurrency) {
        const next = ids.find(id => state[id] === 'pending');
        if (next == null) break;

        // Start call sequence for this order
        state[next] = 'ringing';
        setDial({ ...state });
        addActivity(next, 'Ringing...', 'info');

        // Ringing phase (2-3 seconds)
        const ringingDelay = 2000 + Math.floor(secureRandom() * 1000);
        const t1 = window.setTimeout(() => {
          state[next] = 'connected';
          setDial({ ...state });
          addActivity(next, 'Connected', 'success');

          // Connected phase (1 second)
          const t2 = window.setTimeout(() => {
            state[next] = 'talking';
            setDial({ ...state });
            addActivity(next, 'AI speaking...', 'info');

            // Talking phase (2-4 seconds)
            const talkingDelay = 2000 + Math.floor(secureRandom() * 2000);
            const t3 = window.setTimeout(() => {
              // Determine final outcome (simulated)
              const outcomes = ['confirmed', 'alt_window', 'reschedule', 'no_answer', 'transfer'];
              const weights = [0.5, 0.2, 0.1, 0.1, 0.1];
              const rand = secureRandom();
              let cumulative = 0;
              let outcome = 'confirmed';
              for (let i = 0; i < outcomes.length; i++) {
                cumulative += weights[i];
                if (rand < cumulative) {
                  outcome = outcomes[i];
                  break;
                }
              }

              const order = orders.find(o => o.id === next);
              if (!order) return;

              // Check if transfer
              if (outcome === 'transfer') {
                state[next] = 'transfer_requested';
                setDial({ ...state });
                const transferReasons = [
                  "Need to change delivery time",
                  "Have questions about medications",
                  "Address needs updating"
                ];
                const reason = transferReasons[Math.floor(secureRandom() * transferReasons.length)];
                addActivity(next, `📞 TRANSFER: "${reason}"`, 'transfer');
                setOutcomeStats(prev => ({ ...prev, transfer: prev.transfer + 1 }));

                // Add to live transfer queue immediately
                setLiveTransfers(prev => [...prev, {
                  order: order,
                  transferReason: reason,
                  timestamp: Date.now()
                }]);

                // Brief delay then mark done
                const t4 = window.setTimeout(() => {
                  state[next] = 'done';
                  const completed = Object.values(state).filter(s => s === 'done').length;
                  setDial({ ...state });
                  setProgress(p => ({ ...p, completed }));

                  if (completed < ids.length) {
                    kick();
                  }
                }, 500);
                timersRef.current.push(t4);
              } else {
                // Non-transfer outcome
                state[next] = 'done';
                const completed = Object.values(state).filter(s => s === 'done').length;
                setDial({ ...state });
                setProgress(p => ({ ...p, completed }));

                // Update stats
                setOutcomeStats(prev => {
                  const updated = { ...prev };
                  if (outcome === 'confirmed') updated.confirmed++;
                  else if (outcome === 'alt_window') updated.alt_window++;
                  else if (outcome === 'reschedule') updated.reschedule++;
                  else if (outcome === 'no_answer') updated.no_answer++;
                  return updated;
                });

                addActivity(next, `Completed: ${outcome}`, outcome === 'confirmed' ? 'success' : outcome === 'no_answer' ? 'warning' : 'info');

                if (completed < ids.length) {
                  kick();
                }
              }
            }, talkingDelay);
            timersRef.current.push(t3);
          }, 1000);
          timersRef.current.push(t2);
        }, ringingDelay);
        timersRef.current.push(t1);
      }
    }
    kick();
  }

  function pauseResume() {
    setProgress(p => ({ ...p, paused: !p.paused }));
    if (!progress.paused) {
      // pausing: clear timers
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    } else if (block) {
      // resuming
      runDialer(block.id, block.order_ids, { ...dial });
    }
  }

  function backToSelection() {
    navigate('/autocall');
  }

  const isLiveTransferContext = step === 2 || step === 3;

  // Get transfers from this block
  const transferOrders = useMemo(() => {
    if (!block) return [];
    return block.order_ids
      .map(id => orders.find(o => o.id === id))
      .filter(o => o && o.last_outcome === 'transfer')
      .map(o => {
        return { order: o!, transferReason: o!.transfer_reason ?? 'Customer requested dispatcher' };
      });
  }, [block, orders]);

  const processedOrders = useMemo(() => {
    if (!block) return [];
    return block.order_ids
      .map(id => orders.find(o => o.id === id))
      .filter((o): o is Order => o !== undefined && o.last_outcome !== 'transfer');
  }, [block, orders]);

  function handleInlineEdit(order: Order, transferReason: string) {
    setEditing({
      order,
      outcome: order.last_outcome ?? 'confirmed',
      window: order.last_window ?? order.preferred_window ?? null,
      date: order.delivery_date,
      transferReason: order.transfer_reason ?? transferReason,
      cancellationReason: order.cancellation_reason ?? ''
    });
    setEditingInline(order.id);
  }

  function saveInlineEdit() {
    if (!editing) return;
    if (editing.outcome === 'cancelled' && !editing.cancellationReason?.trim()) {
      alert('Enter a cancellation reason');
      return;
    }

    setHandledTransfers(prev => new Set([...prev, editing.order.id]));
    setEditingInline(null);
    setEditing(null);

    // Auto-advance to next transfer
    const remaining = transferOrders.filter(t => !handledTransfers.has(t.order.id) && t.order.id !== editing.order.id);
    if (remaining.length > 0) {
      const next = remaining[0];
      setTimeout(() => {
        handleInlineEdit(next.order, next.transferReason);
      }, 100);
    }
  }

  function cancelInlineEdit() {
    setEditingInline(null);
    setEditing(null);
  }

  function updateEditing(updates: Partial<EditingState>) {
    if (!editing) return;
    setEditing({ ...editing, ...updates });
  }

  // Handle callback (Five9 simulation)
  function handleCallback(order: Order, transferReason: string) {
    setToast({ message: `Initiating Five9 callback to ${order.phone}...`, type: 'info' });
    setTimeout(() => {
      setToast({ message: `Connected to ${order.phone}. Handle via inline edit below.`, type: 'success' });
      handleInlineEdit(order, transferReason);
      setTimeout(() => setToast(null), 3000);
    }, 2000);
  }

  // Save inline edit in Step 2 (during active calling)
  function saveInlineEditStep2() {
    if (!editing) return;
    if (editing.outcome === 'cancelled' && !editing.cancellationReason?.trim()) {
      alert('Enter a cancellation reason');
      return;
    }

    const editedOrderId = editing.order.id;

    // Update liveTransfers - remove the edited order
    const updatedTransfers = liveTransfers.filter(t => t.order.id !== editedOrderId);

    const updatedHandled = new Set([...handledTransfers, editedOrderId]);
    const remaining = updatedTransfers.filter(t => !updatedHandled.has(t.order.id));

    // Update state
    setLiveTransfers(updatedTransfers);
    setHandledTransfers(updatedHandled);
    setEditingInline(null);
    setEditing(null);

    // Auto-advance to next unhandled transfer
    if (remaining.length > 0) {
      const next = remaining[0];
      setTimeout(() => {
        handleInlineEdit(next.order, next.transferReason);
      }, 100);
    }
  }


  type TransferOrderUpdateInput = {
    outcome: string;
    deliveryDate: string;
    window: WindowBucket | null;
    comments: string;
    notes: string;
  };

  const buildTransferQueueUpdatePayload = (updates: TransferOrderUpdateInput): Partial<{
    outcome: string;
    comments: string;
    notes: string;
    updatedWindow: TransferQueueWindowPayload;
    updatedDate: string;
  }> => {
    const payload: ReturnType<typeof buildTransferQueueUpdatePayload> = {};
    if (updates.outcome) payload.outcome = updates.outcome;
    if (updates.comments) payload.comments = updates.comments;
    if (updates.notes) payload.notes = updates.notes;

    const windowPayload = toTransferQueueWindowPayload(updates.window);

    if (updates.outcome === 'alternate_window' && windowPayload) {
      payload.updatedWindow = windowPayload;
    } else {
      if (updates.deliveryDate) {
        const parsedDate = new Date(updates.deliveryDate);
        if (!Number.isNaN(parsedDate.getTime())) payload.updatedDate = parsedDate.toISOString();
      }
      if (windowPayload) payload.updatedWindow = windowPayload;
    }
    return payload;
  };

  const handleUpdateOrder = async (orderId: string, updates: TransferOrderUpdateInput) => {
    try {
      configureMsalInstance(msalInstance);
      const updateData = buildTransferQueueUpdatePayload(updates);
      if (Object.keys(updateData).length === 0) {
        showToast('No changes to update', 'info');
        return;
      }
      await updateTransferQueue(orderId, updateData);
      hasLoadedRef.current = false;
      await loadTransferQueue(page, pageSize);
      showToast('Order updated successfully', 'success');
    } catch (err) {
      const errorMessage =
        err instanceof ApiError || err instanceof Error ? err.message : 'Failed to update order';
      showToast(errorMessage, 'error');
      console.error('Failed to update transfer queue order:', err);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), type === 'success' ? 3000 : 5000);
  };

  const handleStartManualCall = async (orderId: string, phoneNumber: string): Promise<void> => {
    try {
      configureMsalInstance(msalInstance);
      await startManualTransferQueueCall(orderId, {
        phoneNumber,
        callFlowType: 'dtmf',
      });
      showToast(`Call initiated for ${phoneNumber}`, 'success');
      hasLoadedRef.current = false;
      await loadTransferQueue(page, pageSize);
    } catch (err) {
      const errorMessage =
        err instanceof ApiError || err instanceof Error ? err.message : 'Failed to initiate call';
      showToast(errorMessage, 'error');
      console.error('Failed to initiate manual transfer queue call:', err);
    }
  };

  // Lock order before editing
  const handleLockOrder = async (orderId: string): Promise<boolean> => {
    try {
      configureMsalInstance(msalInstance);
      await lockTransferQueueOrder(orderId);
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 409) {
        const details = err.details as { data?: { lockStatus?: { blockedBy?: string } } } | undefined;
        const blockedBy = details?.data?.lockStatus?.blockedBy;

        // Self-lock: the same user refreshed the page — stale lock still on server.
        // Auto-recover: unlock the stale lock then re-lock so editing can proceed.
        if (blockedBy && blockedBy.toUpperCase() === currentUserId.toUpperCase()) {
          try {
            configureMsalInstance(msalInstance);
            await unlockTransferQueueOrder(orderId);
            await lockTransferQueueOrder(orderId);
            return true;
          } catch {
            // Recovery failed — fall through to generic error
          }
        }

        // Another user holds the lock — block and inform
        const errorMessage = blockedBy
          ? `This order is currently being edited by ${blockedBy}`
          : 'This order is currently being edited by another dispatcher';
        setToast({ message: errorMessage, type: 'error' });
        setTimeout(() => setToast(null), 5000);
        return false;
      }

      const errorMessage =
        err instanceof ApiError || err instanceof Error
          ? err.message
          : 'Failed to lock order';
      setToast({ message: errorMessage, type: 'error' });
      setTimeout(() => setToast(null), 5000);
      return false;
    }
  };

  // Unlock order after cancel / save (best-effort)
  const handleUnlockOrder = (orderId: string): void => {
    configureMsalInstance(msalInstance);
    unlockTransferQueueOrder(orderId).catch((err) => {
      console.error('Failed to unlock order:', err);
    });
  };

  // ── Pagination helpers (mirror Call Audit pattern) ──────────────────────────
  const totalPages = pagination.totalPages;
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const displayCount = `${pagination.total} total records`;

  function getPageNumbers(): Array<number | '…'> {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '…')[] = [1];
    if (page > 3) pages.push('…');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
      pages.push(i);
    }
    if (page < totalPages - 2) pages.push('…');
    pages.push(totalPages);
    return pages;
  }

  // Clears any filter menus (none exist in TQ yet) and navigates to the new page
  function navigatePage(newPage: number) {
    if (newPage === page) return;
    setPage(newPage);
  }

  // Shared fixed-layout style — anchors from below the sticky nav to the bottom
  // of the viewport so only the table body scrolls and the page never scrolls.
  const fixedPageStyle: CSSProperties = {
    position: 'fixed',
    top: 'calc(var(--app-nav-header-height) + var(--app-nav-height))',
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--neutral-gray)',
    padding: '8px 32px 8px',
    boxSizing: 'border-box',
    overflow: 'hidden',
    zIndex: 1,
  };

  // Show transfer queue table (with dummy data if no locationState)
  if (!locationState || !block) {
    return (
      <div className="transfer-queue" style={fixedPageStyle}>
        <div className="page-header" style={{ flexShrink: 0, marginBottom: 8 }}>
          <h1 className="page-title">🔄 Transfer Queue</h1>
          <p className="page-subtitle">Live transfers awaiting dispatcher action</p>
        </div>

        {loading && records.length === 0 && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
            Loading orders...
          </div>
        )}
        {error && !loading && (
          <ErrorState message={error} onRetry={() => loadTransferQueue(page, pageSize)} />
        )}
        {!(loading && records.length === 0) && !error && (
          <>
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <TransferQueueTable
                records={records}
                loading={loading}
                title="Transfer Queue"
                emptyMessage="No orders in transfer queue"
                onUpdateOrder={handleUpdateOrder}
                onLockOrder={handleLockOrder}
                onUnlockOrder={handleUnlockOrder}
                onStartManualCall={handleStartManualCall}
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                onSearchSubmit={handleSearchSubmit}
                cityFilter={cityFilter}
                onCityFilterChange={handleCityFilterChange}
                cityOptions={allCities}
                statusFilter={statusFilter}
                onStatusFilterChange={handleStatusFilterChange}
                outcomeFilter={outcomeFilter}
                onOutcomeFilterChange={handleOutcomeFilterChange}
                whiteGloveFilter={whiteGloveFilter}
                onWhiteGloveFilterChange={handleWhiteGloveFilterChange}
              />
            </div>

            {/* ── Pinned pagination bar ── */}
            <div
              className="autocall-pagination"
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  flexWrap: 'wrap',
                  padding: '10px 0',
                  borderTop: '1px solid var(--border)',
                  background: 'var(--neutral-gray)',
                }}
              >
                <span style={{ fontWeight: 500 }}>Orders per page:</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <button
                      key={size}
                      type="button"
                      className={pageSize === size ? 'primary' : 'secondary'}
                      onClick={() => { setPageSize(size); navigatePage(1); }}
                      style={{ minWidth: 40 }}
                    >
                      {size}
                    </button>
                  ))}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => navigatePage(Math.max(1, page - 1))}
                    disabled={!canPrev}
                    style={{ minWidth: 36 }}
                  >
                    ‹
                  </button>
                  {getPageNumbers().map((pg, i, arr) =>
                    pg === '…' ? (
                      <span key={`ellipsis-${arr[i - 1]}-${arr[i + 1]}`} style={{ padding: '0 6px', color: 'var(--text-muted)' }}>…</span>
                    ) : (
                      <button
                        key={pg}
                        type="button"
                        className={page === pg ? 'primary' : 'secondary'}
                        onClick={() => navigatePage(pg)}
                        style={{ minWidth: 36 }}
                      >
                        {pg}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => navigatePage(Math.min(totalPages, page + 1))}
                    disabled={!canNext}
                    style={{ minWidth: 36 }}
                  >
                    ›
                  </button>
                </div>

                <span style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {displayCount}
                </span>

                <button
                  type="button"
                  className="secondary"
                  onClick={() => loadTransferQueue(page, pageSize)}
                  aria-label="Refresh"
                >
                  Refresh
                </button>
            </div>
          </>
        )}

        <ToastComponent toast={toast} />
      </div>
    );
  }

  return (
    <div className="transfer-queue" style={fixedPageStyle}>
      <div className="page-header" style={{ flexShrink: 0, marginBottom: 8 }}>
        <h1 className="page-title">🔄 Transfer Queue</h1>
        <p className="page-subtitle">Live Calling & Transfer Management</p>
      </div>

      {/* Step 2: Transfer Queue Table */}
      {step === 2 && (
        <>
          {loading && records.length === 0 && (
            <CenteredLoading muted />
          )}
          {error && !loading && (
            <ErrorState message={error} onRetry={() => loadTransferQueue(page, pageSize)} />
          )}
          {!(loading && records.length === 0) && !error && (
            <>
              <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <TransferQueueTable
                  records={records}
                  loading={loading}
                  title="Transfer Queue"
                  emptyMessage="No orders in transfer queue"
                  onUpdateOrder={handleUpdateOrder}
                  onLockOrder={handleLockOrder}
                  onUnlockOrder={handleUnlockOrder}
                  onStartManualCall={handleStartManualCall}
                  searchQuery={searchQuery}
                  onSearchChange={handleSearchChange}
                  onSearchSubmit={handleSearchSubmit}
                  cityFilter={cityFilter}
                  onCityFilterChange={handleCityFilterChange}
                  cityOptions={allCities}
                  statusFilter={statusFilter}
                  onStatusFilterChange={handleStatusFilterChange}
                  outcomeFilter={outcomeFilter}
                  onOutcomeFilterChange={handleOutcomeFilterChange}
                  whiteGloveFilter={whiteGloveFilter}
                  onWhiteGloveFilterChange={handleWhiteGloveFilterChange}
                />
              </div>

              <div
                className="autocall-pagination"
                  style={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    flexWrap: 'wrap',
                    padding: '10px 0',
                    borderTop: '1px solid var(--border)',
                    background: 'var(--neutral-gray)',
                  }}
                >
                  <span style={{ fontWeight: 500 }}>Orders per page:</span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <button
                        key={size}
                        type="button"
                        className={pageSize === size ? 'primary' : 'secondary'}
                        onClick={() => { setPageSize(size); navigatePage(1); }}
                        style={{ minWidth: 40 }}
                      >
                        {size}
                      </button>
                    ))}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => navigatePage(Math.max(1, page - 1))}
                      disabled={!canPrev}
                      style={{ minWidth: 36 }}
                    >
                      ‹
                    </button>
                    {getPageNumbers().map((pg, i, arr) =>
                      pg === '…' ? (
                        <span key={`ellipsis-${arr[i - 1]}-${arr[i + 1]}`} style={{ padding: '0 6px', color: 'var(--text-muted)' }}>…</span>
                      ) : (
                        <button
                          key={pg}
                          type="button"
                          className={page === pg ? 'primary' : 'secondary'}
                          onClick={() => navigatePage(pg)}
                          style={{ minWidth: 36 }}
                        >
                          {pg}
                        </button>
                      )
                    )}
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => navigatePage(Math.min(totalPages, page + 1))}
                      disabled={!canNext}
                      style={{ minWidth: 36 }}
                    >
                      ›
                    </button>
                  </div>

                  <span style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {displayCount}
                  </span>

                  <button
                    type="button"
                    className="secondary"
                    onClick={() => loadTransferQueue(page, pageSize)}
                    aria-label="Refresh"
                  >
                    Refresh
                  </button>
              </div>
            </>
          )}
        </>
      )}

      {/* Step 3: Review */}
      {step === 3 && block && (
        <Step3Review
          transferOrders={transferOrders}
          processedOrders={processedOrders}
          handledTransfers={handledTransfers}
          editingInline={editingInline}
          editing={editing}
          onEdit={handleInlineEdit}
          onCancelEdit={cancelInlineEdit}
          onSaveEdit={saveInlineEdit}
          onUpdateEditing={updateEditing}
          onBackToSelection={backToSelection}
        />
      )}

      {/* Edit panel modal */}
      {editing && !editingInline && (
        <EditModal
          editing={editing}
          step={step}
          isLiveTransferContext={isLiveTransferContext}
          onCancel={cancelInlineEdit}
          onSaveStep2={saveInlineEditStep2}
          onSaveStep3={saveInlineEdit}
          onUpdateOrders={() => { }}
          onUpdateEditing={updateEditing}
        />
      )}

      <ToastComponent toast={toast} />
    </div>
  );
}
